<template>
  <div class="prompt-editor">
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <Button
          :icon="expanded ? 'pi pi-compress' : 'pi pi-expand'"
          :label="expanded ? 'Contraer' : 'Pantalla completa'"
          text size="small"
          @click="expanded = !expanded"
        />
        <Button
          icon="pi pi-eye"
          :label="showPreview ? 'Ocultar preview' : 'Preview'"
          :severity="showPreview ? 'primary' : 'secondary'"
          text size="small"
          @click="showPreview = !showPreview"
        />
      </div>
      <div class="toolbar-right">
        <span class="char-count">{{ localPrompt.length }} chars</span>
      </div>
    </div>

    <div class="editor-main" :class="{ expanded }">
      <!-- Editor -->
      <div class="editor-pane">
        <div class="variables-bar">
          <span class="var-label">Variables:</span>
          <button
            v-for="v in variables"
            :key="v.key"
            type="button"
            class="var-chip"
            v-tooltip="v.description"
            @click="insertVariable(v.key)"
          >
            {{ v.key }}
          </button>
        </div>
        <Textarea
          ref="textareaRef"
          v-model="localPrompt"
          class="prompt-textarea"
          :class="{ 'p-invalid': error }"
          :autoResize="false"
          :rows="expanded ? 30 : 15"
          placeholder="Escribe el prompt del sistema aquí... Usa {{variable}} para placeholders dinámicos."
        />
        <small v-if="error" class="p-error">{{ error }}</small>
      </div>

      <!-- Preview pane -->
      <div v-if="showPreview" class="preview-pane">
        <div class="preview-header">
          <i class="pi pi-eye" /> Vista previa ({{ agentName }})
        </div>
        <div class="preview-content" v-html="renderedPreview" />
      </div>
    </div>

    <!-- Save form -->
    <div class="save-bar">
      <InputText
        v-model="changeNote"
        placeholder="Nota de cambio (opcional)"
        class="change-note-input"
      />
      <Button
        label="Guardar nueva versión"
        icon="pi pi-check"
        :loading="saving"
        :disabled="!localPrompt.trim() || !hasChanges"
        @click="savePrompt"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import api from '../../services/api';

const props = defineProps({
  agentId: { type: Number, required: true },
  agentName: { type: String, default: '' },
  initialPrompt: { type: String, default: '' },
  variables: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved', 'version-changed']);
const toast = useToast();

const localPrompt = ref(props.initialPrompt);
const changeNote = ref('');
const saving = ref(false);
const expanded = ref(false);
const showPreview = ref(false);
const error = ref('');
const textareaRef = ref(null);

// Detectar cambios
const hasChanges = computed(() => localPrompt.value !== props.initialPrompt);

// Watch para sincronizar con el prop
watch(() => props.initialPrompt, (val) => {
  localPrompt.value = val;
});

// Preview renderizado (reemplaza variables con valores de ejemplo)
const renderedPreview = computed(() => {
  let text = localPrompt.value;
  const replacements = {
    '{{tenant_name}}': 'Empresa Demo S.A.S.',
    '{{agent_name}}': props.agentName,
    '{{agent_category}}': 'financiero',
    '{{user_name}}': 'Usuario Demo',
    '{{current_date}}': new Date().toLocaleDateString('es-CO'),
    '{{rag_context}}': '[Contexto RAG recuperado de las bases de conocimiento]',
  };
  for (const [key, val] of Object.entries(replacements)) {
    text = text.replaceAll(key, `<strong style="color:#2563eb">${val}</strong>`);
  }
  // Convertir markdown básico a HTML
  text = text.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  text = text.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  text = text.replace(/^- (.+)$/gm, '<li>$1</li>');
  text = text.replace(/\n/g, '<br>');
  return text;
});

const insertVariable = (varKey) => {
  const textarea = textareaRef.value?.$el;
  if (!textarea) {
    localPrompt.value += varKey;
    return;
  }
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = localPrompt.value.substring(0, start);
  const after = localPrompt.value.substring(end);
  localPrompt.value = before + varKey + after;
  // Restaurar cursor
  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + varKey.length;
    textarea.focus();
  }, 10);
};

const savePrompt = async () => {
  if (!localPrompt.value.trim()) {
    error.value = 'El prompt no puede estar vacío';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const res = await api.put(`/admin/agents/${props.agentId}/prompt`, {
      prompt: localPrompt.value,
      change_note: changeNote.value || undefined,
    });
    toast.add({
      severity: 'success',
      summary: 'Guardado',
      detail: `Prompt actualizado a versión ${res.data.version}`,
      life: 2500,
    });
    changeNote.value = '';
    emit('saved', { prompt: localPrompt.value, version: res.data.version });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.detail || e.message,
      life: 3000,
    });
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.prompt-editor { display: flex; flex-direction: column; gap: 0.75rem; }

.editor-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.25rem 0;
}
.toolbar-left, .toolbar-right { display: flex; align-items: center; gap: 0.5rem; }
.char-count { font-size: 0.75rem; color: #9ca3af; }

.editor-main {
  display: grid; grid-template-columns: 1fr;
  gap: 1rem;
  transition: all 0.3s ease;
}
.editor-main.expanded {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  z-index: 1000; background: white; padding: 2rem;
  grid-template-columns: 1fr;
  overflow: auto;
}
.editor-main:not(.expanded):has(.preview-pane) {
  grid-template-columns: 1fr 1fr;
}

.editor-pane { display: flex; flex-direction: column; gap: 0.5rem; }

.variables-bar {
  display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;
  padding: 0.4rem 0.6rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;
}
.var-label { font-size: 0.72rem; font-weight: 600; color: #6b7280; }
.var-chip {
  font-size: 0.7rem; padding: 0.15rem 0.5rem;
  background: #e0f2fe; color: #0369a1; border: 1px solid #bae6fd;
  border-radius: 12px; cursor: pointer; transition: all 0.15s;
  font-family: monospace;
}
.var-chip:hover { background: #bae6fd; }

.prompt-textarea {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.82rem; line-height: 1.6;
  width: 100%;
}

.preview-pane {
  border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;
}
.preview-header {
  padding: 0.5rem 0.75rem; background: #f1f5f9; border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem; font-weight: 600; color: #475569;
  display: flex; align-items: center; gap: 0.4rem;
}
.preview-content {
  padding: 1rem; font-size: 0.82rem; line-height: 1.6;
  max-height: 500px; overflow-y: auto;
  color: #374151;
}

.save-bar {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.75rem 0; border-top: 1px solid #f1f5f9;
}
.change-note-input { flex: 1; }
</style>
